import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { logoService } from '../../services/logoService';

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  padding: 0 16px;
  width: 163px;
  overflow: hidden;

  /* Responsive container sizing */
  @media (max-width: 375px) {
    width: 140px;
  }

  @media (min-width: 376px) and (max-width: 414px) {
    width: 150px;
  }

  @media (min-width: 415px) and (max-width: 768px) {
    width: min(163px, 42.5vw);
  }

  @media (min-width: 769px) {
    width: 163px;
  }
`;

const Logo = styled.img`
  width: 100%;
  height: auto;
  max-height: 100%;
  object-fit: contain;
  object-position: left center;
  display: block;

  /* Ensure compatibility with iOS and Android */
  -webkit-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
`;

interface HeaderLogoProps {
  className?: string;
}

const HeaderLogo: React.FC<HeaderLogoProps> = ({ className }) => {
  const { user } = useAuth();
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
    const loadLogo = async () => {
      setIsLoading(true);
      try {
        // Get the logo filename from user's distributor info
        const logoFilename = user?.distributor?.logo_filename || null;

        // Get the logo URL (with caching)
        const url = await logoService.getLogoUrl(logoFilename);
        setLogoUrl(url);
      } catch (error) {
        console.error('Failed to load logo:', error);
        // Fallback to default logo
        setLogoUrl(logoService.getDefaultLogoUrl());
      } finally {
        setIsLoading(false);
      }
    };

    // Only load logo if user is authenticated
    if (user) {
      loadLogo();
    } else {
      // Set default logo for unauthenticated users
      setLogoUrl(logoService.getDefaultLogoUrl());
      setIsLoading(false);
    }
  }, [user?.distributor?.logo_filename, user]);

  // Show loading state or default logo while loading
  if (isLoading) {
    return (
      <LogoContainer className={className}>
        <Logo
          src={logoService.getDefaultLogoUrl()}
          alt="Company Logo"
        />
      </LogoContainer>
    );
  }

  return (
    <LogoContainer className={className}>
      <Logo
        src={logoUrl}
        alt="Company Logo"
        onError={() => {
          // Fallback to default logo if the distributor logo fails to load
          setLogoUrl(logoService.getDefaultLogoUrl());
        }}
      />
    </LogoContainer>
  );
};

export default HeaderLogo;
